import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Text, Button, IconButton } from "@zillow/constellation-10";
import { IconArrowRightFilled } from "@zillow/constellation-icons";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import UpsellCard from "./UpsellCard.esm.js";
import UpsellHeading from "./UpsellHeading.esm.js";
import { BREAKPOINT_TABLET, BREAKPOINT_DESKTOP } from "./constants.esm.js";
import { usePageProps } from "@zillow/cms-utils";
const SM_MD_LG_SIZES = ["sm", "md", "lg"];
const XS_CONTAINER_CSS = {
  display: "flex",
  position: "relative",
  flexDirection: "column",
  xl: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  }
};
const Upsell = ({
  body,
  button = "Learn More",
  className,
  color,
  editorMode = false,
  heading,
  id,
  image,
  legal,
  size,
  url
}) => {
  const pageProps = usePageProps();
  const isSmMdLg = SM_MD_LG_SIZES.includes(size);
  const hasNonXsArrowCta = button === "" && isSmMdLg;
  const hasImage = size === "md" && !!image;
  function onClick() {
    if (!editorMode) {
      event({
        // https://dataportal.zillowgroup.net/event-registry/2048
        envelope: {
          event_type_id: "2048",
          event_type_version_id: "1",
          event_template_id: "126",
          event_template_version_id: "1"
          // Master ID: 2284.2.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: "interaction",
          trigger_location_nm: "cms",
          trigger_source_nm: "cms_upsell",
          trigger_object_nm: "no_trigger_object"
        },
        semantic: {
          semantic_event_nm: "cms_click"
        },
        cms_info: {
          block_id: id || "",
          block_type_cd: "upsell",
          block_displayed_txt: `${heading}
${button}`,
          block_target_url: url,
          block_target_window_txt: "",
          ...getCmsInfoForPage(pageProps)
        }
      });
    }
  }
  const learnMoreMarginTop = size === "sm" ? "layout.looser" : {
    base: "layout.looser",
    [BREAKPOINT_TABLET]: "looser"
  };
  const arrowMarginTop = size === "xs" ? {
    base: "layout.looser",
    [BREAKPOINT_TABLET]: "layout.vast.default",
    [BREAKPOINT_DESKTOP]: "none"
  } : void 0;
  const bodyTextCss = {
    color: "var(--text)",
    fontSize: {
      base: "body-sm",
      md: "body"
    },
    lineHeight: {
      base: "body-xs",
      md: "body"
    },
    marginBlockStart: "0",
    marginRight: "0",
    marginLeft: "0",
    marginBottom: hasNonXsArrowCta && !legal ? "0" : "default"
  };
  const legalTextCss = {
    color: "var(--text-legal)",
    fontSize: "body-sm",
    lineHeight: "body-xs",
    marginBottom: hasNonXsArrowCta ? "0" : "default"
  };
  const learnMoreButtonCss = {
    backgroundColor: "var(--button)",
    borderColor: "var(--button)",
    color: "var(--button-text)",
    '& [data-c11n-component="Button.Label"]': {
      color: "var(--button-text)",
      // v8's button centered its label; v10's Button.Label is a block-level
      // span defaulting to text-align:start, so a wrapping (2-line) label
      // hugs the left. Restore v8's centered wrap. No effect on single-line.
      textAlign: "center"
    },
    marginBlockStart: learnMoreMarginTop,
    ...editorMode && {
      // editorMode is a runtime prop, so Panda's static extractor cannot
      // see these values at build time — each is safelisted in both
      // panda.config.ts files.
      paddingBlock: "[6px]",
      paddingInline: "[8px]",
      borderRadius: "[4px]",
      display: "inline-block",
      userSelect: "auto",
      "&::after": {
        pointerEvents: "none"
      }
    },
    "&:visited": {
      backgroundColor: "var(--button)",
      borderColor: "var(--button)",
      color: "var(--button-text)"
    }
  };
  const arrowButtonCss = {
    backgroundColor: "var(--button)",
    borderColor: "var(--button)",
    color: "var(--background)",
    fontWeight: "normal",
    marginLeft: "auto",
    marginBlockStart: arrowMarginTop,
    // In the arrow-only (button='') layout the arrow is a child of a row
    // flexbox next to the text column. Without this, flex-shrink:1 +
    // min-width:auto lets the text squeeze the circle horizontally into an
    // oval (24x48 instead of 48x48). Not needed for xs (column layout) but
    // harmless there.
    flexShrink: 0,
    // v8's ColoredIconButton set align-self: flex-end so the arrow sits at
    // the bottom of the row (aligned with the last line of body text). v10's
    // non-xs container uses alignItems: center, which would otherwise center
    // the arrow vertically — this restores the v8 bottom alignment.
    alignSelf: "flex-end",
    // Match the base arrow color (the card background paints the glyph). v8 had
    // no :visited override, so the icon stayed var(--background) after visiting;
    // keep it consistent here rather than flipping to var(--button-text) (white).
    "&:visited": {
      backgroundColor: "var(--button)",
      borderColor: "var(--button)",
      color: "var(--background)"
    }
  };
  return /* @__PURE__ */ jsx(UpsellCard, { button, className, color, id: id ?? "", size, children: /* @__PURE__ */ jsxs(Box, { css: size === "xs" ? XS_CONTAINER_CSS : {
    display: "flex",
    position: "relative"
    // No alignItems: v8 left this at the flex default
    // (stretch) so the md image column stretches to the
    // card height. An explicit 'center' here (as an
    // earlier v10 pass had) collapsed the image to its
    // intrinsic height and centered it. The arrow-only
    // CTA sets its own alignSelf: flex-end, so it does
    // not rely on a container alignItems value.
  }, children: [
    /* @__PURE__ */ jsxs(Box, { css: {
      ...hasNonXsArrowCta && {
        paddingRight: "default",
        md: {
          paddingRight: "looser",
          "& .Upsell__Body, & .Upsell__Legal": {
            maxWidth: "250px"
          }
        }
      },
      ...hasImage && {
        paddingRight: "0",
        xl: {
          paddingRight: "layout.vast.default"
        }
      }
    }, children: [
      /* @__PURE__ */ jsx(UpsellHeading, { size, children: heading }),
      isSmMdLg && /* @__PURE__ */ jsx(Text, { asChild: true, textStyle: "body-sm", css: bodyTextCss, children: /* @__PURE__ */ jsx("p", { className: "Upsell__Body", children: body }) }),
      isSmMdLg && !!legal && /* @__PURE__ */ jsx(Text, { asChild: true, textStyle: "body-sm", css: legalTextCss, children: /* @__PURE__ */ jsx("p", { className: "Upsell__Legal", children: legal }) }),
      isSmMdLg && button !== "" && /* @__PURE__ */ jsx(Button, { asChild: !editorMode, tone: "neutral", emphasis: "filled", className: "Upsell__Button", css: learnMoreButtonCss, onClick: editorMode ? void 0 : yieldCallback(onClick), children: editorMode ? /* @__PURE__ */ jsx("div", { children: button }) : /* @__PURE__ */ jsx("a", { href: url, children: button }) })
    ] }),
    (size === "xs" || hasNonXsArrowCta) && /* @__PURE__ */ jsx(IconButton, { asChild: true, icon: /* @__PURE__ */ jsx(IconArrowRightFilled, {}), title: button, shape: "circle", tone: "neutral", emphasis: "filled", className: "Upsell__Button", css: arrowButtonCss, children: /* @__PURE__ */ jsx("a", { href: url, onClick: yieldCallback(onClick) }) }),
    hasImage && /* @__PURE__ */ jsx(Box, { css: {
      display: "none",
      xl: {
        display: "block"
      },
      width: "50%",
      flex: "none",
      "& img": {
        display: "block",
        height: "100%",
        width: "100%",
        objectFit: "cover",
        borderRadius: "obj.soft"
      }
    }, children: image })
  ] }) });
};
export {
  Upsell as default
};
